import { Accordion, AccordionDetails, AccordionSummary, Alert, Avatar, Box, Button, Container, FormControl, FormControlLabel, Grid, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import api from "lib/api/api";
import Meta from "../Meta/Meta";
import { CONTACT_EMAIL, RECRUITMENT_EMAIL } from "config/contact";

const ServiceLandingPage = ({ service }) => {
  const { t, i18n } = useTranslation("ui");
  const language = i18n.language?.split("-")[0] || "en";
  const isArabic = language === "ar";
  const navigate = useNavigate();
  const [requestForm, setRequestForm] = useState({ company: "", contactName: "", email: "", phone: "", lookingFor: "azubi", position: "", profile: "", message: "" });
  const [requestStatus, setRequestStatus] = useState({ submitting: false, success: "", error: "" });
  const getText = (key, fallback = "") =>
    t(key, {
      defaultValue: t(key, { lng: "en", defaultValue: fallback }),
    });
  const getObject = (key, fallback) => {
    const value = t(key, { returnObjects: true, defaultValue: fallback });
    return typeof value === "string" ? fallback : value;
  };
  const translatedContent = service.contentKey
    ? {
        title: getText(`${service.contentKey}.title`),
        heroLabel: getText(`${service.contentKey}.heroLabel`),
        heroKicker: getText(`${service.contentKey}.heroKicker`),
        description: getText(`${service.contentKey}.description`),
        trustLine: getText(`${service.contentKey}.trustLine`),
        cta: getText(`${service.contentKey}.cta`),
        sectionTitle: getText(`${service.contentKey}.sectionTitle`),
        person: getObject(`${service.contentKey}.person`, {}),
        body: getObject(`${service.contentKey}.body`, []),
        points: getObject(`${service.contentKey}.points`, []),
        whyTitle: getText(`${service.contentKey}.whyTitle`),
        whyParagraphs: getObject(`${service.contentKey}.whyParagraphs`, []),
        screeningTitle: getText(`${service.contentKey}.screeningTitle`),
        screeningItems: getObject(`${service.contentKey}.screeningItems`, []),
        whyDetail: getText(`${service.contentKey}.whyDetail`),
        benefitsTitle: getText(`${service.contentKey}.benefitsTitle`),
        benefitItems: getObject(`${service.contentKey}.benefitItems`, []),
        processTitle: getText(`${service.contentKey}.processTitle`),
        processSteps: getObject(`${service.contentKey}.processSteps`, []),
        processClosing: getText(`${service.contentKey}.processClosing`),
        requestTitle: getText(`${service.contentKey}.requestTitle`),
        requestIntro: getText(`${service.contentKey}.requestIntro`),
        requestFields: getObject(`${service.contentKey}.requestFields`, {}),
        requestOptions: getObject(`${service.contentKey}.requestOptions`, {}),
        requestSubmit: getText(`${service.contentKey}.requestSubmit`),
        requestSuccess: getText(`${service.contentKey}.requestSuccess`),
        requestSuccessNoEmail: getText(`${service.contentKey}.requestSuccessNoEmail`),
        requestEmailError: getText(`${service.contentKey}.requestEmailError`),
        requestError: getText(`${service.contentKey}.requestError`),
        requestDirect: getText(`${service.contentKey}.requestDirect`),
        requestWrite: getText(`${service.contentKey}.requestWrite`),
      }
    : null;
  const content = translatedContent || service.translations?.[language] || service.translations?.en || (isArabic ? service.ar : service.en);
  const heroPosition = service.heroPosition || "center";
  const heroContentSx = service.heroContentSx || {};
  const heroTitleSx = service.heroTitleSx || {};
  const heroDescriptionSx = service.heroDescriptionSx || {};
  const heroButtonSx = service.heroButtonSx || {};
  const contentContainerSx = service.contentContainerSx || {};

  const submitEmployerRequest = async (event) => {
    event.preventDefault();
    setRequestStatus({ submitting: true, success: "", error: "" });
    try {
      const candidateTypes = { azubi: "AZUBI", professional: "SKILLED_PROFESSIONAL", both: "BOTH" };
      const response = await api.post("/employer-recruitment-inquiries", {
        companyName: requestForm.company,
        contactPerson: requestForm.contactName,
        email: requestForm.email,
        phone: requestForm.phone || null,
        candidateType: candidateTypes[requestForm.lookingFor],
        positionOrIndustry: requestForm.position,
        preferredProfile: requestForm.profile || null,
        message: requestForm.message || null,
      });
      setRequestForm({ company: "", contactName: "", email: "", phone: "", lookingFor: "azubi", position: "", profile: "", message: "" });
      setRequestStatus({
        submitting: false,
        success: response.data?.confirmationEmailSent ? content.requestSuccess : content.requestSuccessNoEmail,
        error: "",
      });
    } catch (error) {
      const emailDeliveryFailed = error.response?.status === 400 && requestForm.email.includes("@");
      setRequestStatus({ submitting: false, success: "", error: emailDeliveryFailed ? content.requestEmailError : content.requestError });
    }
  };

  return (
    <>
      <Meta
        title={`ALGermanist - ${content.title}`}
        description={content.description}
        keywords={content.title}
      />
      <Box dir={isArabic ? "rtl" : "ltr"}>
        <Box
          sx={{
            backgroundImage: `linear-gradient(180deg, rgba(0,0,0,.55), rgba(0,0,0,.55)), url("${service.image}")`,
            backgroundPosition: heroPosition,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            color: "#fff",
            py: { xs: 8, md: 14 },
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ maxWidth: 760, textAlign: isArabic ? "right" : "left", ...heroContentSx }}>
              {content.heroLabel && (
                <Typography sx={{ color: "#FFD600", fontWeight: 800, mb: 1, fontSize: { xs: "1rem", md: "1.15rem" } }}>
                  {content.heroLabel}
                </Typography>
              )}
              {content.heroKicker && (
                <Typography variant="h5" fontWeight={800} sx={{ mb: 1.5 }}>
                  {content.heroKicker}
                </Typography>
              )}
              <Typography
                variant="h3"
                fontWeight={700}
                sx={{
                  mb: 3,
                  lineHeight: 1.18,
                  letterSpacing: 0,
                  fontSize: { xs: "2rem", sm: "2.45rem", md: "3rem" },
                  overflowWrap: "break-word",
                  ...heroTitleSx,
                }}
              >
                {content.title}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  opacity: 0.95,
                  mb: 4,
                  whiteSpace: "pre-line",
                  letterSpacing: 0,
                  lineHeight: 1.55,
                  ...heroDescriptionSx,
                }}
              >
                {content.description}
              </Typography>
              {content.trustLine && (
                <Typography
                  sx={{
                    mb: 3,
                    py: 1.25,
                    px: 1.75,
                    borderInlineStart: "4px solid #FFD600",
                    bgcolor: "rgba(0,0,0,0.28)",
                    fontWeight: 700,
                    lineHeight: 1.55,
                  }}
                >
                  {content.trustLine}
                </Typography>
              )}
              <Button
                variant="contained"
                onClick={() => navigate("/contact")}
                sx={{
                  bgcolor: "#FFD600",
                  color: "#111",
                  borderRadius: 4,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": { bgcolor: "#e5c400" },
                  ...heroButtonSx,
                }}
              >
                {content.cta}
              </Button>
            </Box>
          </Container>
        </Box>

        <Container maxWidth="lg" sx={{ py: { xs: 5, md: 8 }, ...contentContainerSx }}>
          {service.layout === "person-content" ? (
            <Grid container spacing={4} alignItems="center">
              {!service.personInRequest && <Grid item xs={12} md={5}>
                <Box
                  role={service.person.link ? "link" : undefined}
                  tabIndex={service.person.link ? 0 : undefined}
                  onClick={() => service.person.link && navigate(service.person.link)}
                  onKeyDown={(event) => {
                    if (service.person.link && (event.key === "Enter" || event.key === " ")) navigate(service.person.link);
                  }}
                  sx={{
                    bgcolor: "#F8FAFB",
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid #E4EAEA",
                    cursor: service.person.link ? "pointer" : "default",
                  }}
                >
                  {service.person.image ? (
                    <Box
                      component="img"
                      src={service.person.image}
                      alt={content.person.name}
                      sx={{
                        display: "block",
                        width: "100%",
                        height: { xs: 320, md: 460 },
                        objectFit: "cover",
                        objectPosition: "center",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: { xs: 320, md: 460 },
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "#EEF3F4",
                      }}
                    >
                      <Avatar
                        sx={{
                          width: { xs: 120, md: 160 },
                          height: { xs: 120, md: 160 },
                          bgcolor: "#FFD600",
                          color: "#111",
                          fontSize: { xs: "2.5rem", md: "3.5rem" },
                          fontWeight: 800,
                        }}
                      >
                        {content.person.initials}
                      </Avatar>
                    </Box>
                  )}
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="h6" fontWeight={800}>
                      {content.person.name}
                    </Typography>
                    <Typography color="text.secondary">{content.person.role}</Typography>
                  </Box>
                </Box>
              </Grid>}
              {!service.hideMainContent && <Grid item xs={12} md={service.personInRequest ? 12 : 7}>
                <Box sx={{ textAlign: isArabic ? "right" : "left" }}>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{
                      mb: 3,
                      lineHeight: 1.2,
                      letterSpacing: 0,
                      fontSize: { xs: "1.7rem", md: "2.25rem" },
                      overflowWrap: "break-word",
                    }}
                  >
                    {content.sectionTitle}
                  </Typography>
                  {content.body.map((paragraph) => (
                    <Typography
                      key={paragraph}
                      color="text.secondary"
                      sx={{
                        mb: 2.5,
                        fontSize: { xs: "1rem", md: "1.08rem" },
                        lineHeight: 1.85,
                      }}
                    >
                      {paragraph}
                    </Typography>
                  ))}
                  {!service.hideContentCta && (
                    <Button
                      variant="contained"
                      onClick={() => navigate("/contact")}
                      sx={{
                        mt: 2,
                        bgcolor: "#FFD600",
                        color: "#111",
                        borderRadius: 4,
                        px: 4,
                        py: 1.4,
                        fontWeight: 700,
                        textTransform: "none",
                        boxShadow: "none",
                        "&:hover": { bgcolor: "#e5c400" },
                      }}
                    >
                      {content.cta}
                    </Button>
                  )}
                </Box>
              </Grid>}
              {(content.whyTitle || content.processTitle || content.requestTitle) && (
                <Grid item xs={12}>
                  <Box sx={{ mt: { xs: 1, md: 2 }, textAlign: isArabic ? "right" : "left" }}>
                    {content.whyTitle && (
                      <Accordion disableGutters sx={{ mb: 2, border: "1px solid #E4EAEA", borderRadius: "12px !important", boxShadow: "none", overflow: "hidden", "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2.5, py: 1 }}><Typography variant="h6" fontWeight={800}>{content.whyTitle}</Typography></AccordionSummary>
                        <AccordionDetails sx={{ px: { xs: 2.5, md: 4 }, pb: 3 }}>
                          {content.whyParagraphs.map((paragraph) => <Typography key={paragraph} sx={{ mb: 2, lineHeight: 1.8 }}>{paragraph}</Typography>)}
                          {content.screeningTitle && <Typography fontWeight={800} sx={{ mb: 1 }}>{content.screeningTitle}</Typography>}
                          {content.screeningItems.length > 0 && <Box component="ul" sx={{ mt: 0, mb: 2.5, ps: 3, lineHeight: 1.9 }}>{content.screeningItems.map((item) => <li key={item}>{item}</li>)}</Box>}
                          {content.whyDetail && <Typography sx={{ mb: 2.5, lineHeight: 1.8 }}>{content.whyDetail}</Typography>}
                          {content.benefitsTitle && <Typography fontWeight={800} sx={{ mb: 1 }}>{content.benefitsTitle}</Typography>}
                          {content.benefitItems.length > 0 && <Box component="ul" sx={{ m: 0, ps: 3, lineHeight: 1.9 }}>{content.benefitItems.map((item) => <li key={item}>{item}</li>)}</Box>}
                        </AccordionDetails>
                      </Accordion>
                    )}
                    {content.processTitle && (
                      <Accordion disableGutters sx={{ border: "1px solid #E4EAEA", borderRadius: "12px !important", boxShadow: "none", overflow: "hidden", "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2.5, py: 1 }}><Typography variant="h6" fontWeight={800}>{content.processTitle}</Typography></AccordionSummary>
                        <AccordionDetails sx={{ px: { xs: 2, md: 3 }, pb: 3 }}>
                          <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 1.5, scrollSnapType: "x proximity" }}>
                            {content.processSteps.map((step, index) => (
                              <Box key={step.title} sx={{ flex: "1 0 185px", minWidth: 185, p: 2, borderRadius: 2.5, bgcolor: "#F8FAFB", border: "1px solid #E4EAEA", scrollSnapAlign: "start" }}>
                                <Box sx={{ width: 36, height: 36, mb: 1.5, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: "#FFD600", color: "#111", fontWeight: 900 }}>{index + 1}</Box>
                                <Typography fontWeight={800} sx={{ mb: 0.75 }}>{step.title}</Typography>
                                <Typography color="text.secondary" sx={{ lineHeight: 1.65 }}>{step.text}</Typography>
                              </Box>
                            ))}
                          </Box>
                          {content.processClosing && <Typography sx={{ mt: 2, p: 2, bgcolor: "#FFF8D6", borderRadius: 2, fontWeight: 700, lineHeight: 1.75 }}>{content.processClosing}</Typography>}
                        </AccordionDetails>
                      </Accordion>
                    )}
                    {content.requestTitle && (
                      <Accordion defaultExpanded={Boolean(service.defaultRequestExpanded)} disableGutters sx={{ mt: 2, border: "1px solid #E4EAEA", borderRadius: "12px !important", boxShadow: "none", overflow: "hidden", "&:before": { display: "none" } }}>
                        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ px: 2.5, py: 1 }}><Typography variant="h6" fontWeight={800}>{content.requestTitle}</Typography></AccordionSummary>
                        <AccordionDetails sx={{ px: { xs: 2.5, md: 4 }, pb: 4 }}>
                          {!service.simpleContact && <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>{content.requestIntro}</Typography>}
                          {service.simpleContact ? (
                            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: service.personInRequest ? "minmax(0, 1.2fr) minmax(300px, .8fr)" : "minmax(0, 1fr)" }, gap: { xs: 3, md: 4 }, alignItems: "center", direction: "ltr" }}>
                              <Box dir={isArabic ? "rtl" : "ltr"} sx={{ textAlign: isArabic ? "right" : "left" }}>
                                <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.8 }}>{content.requestIntro}</Typography>
                                {service.personInRequest && <>
                                  <Typography variant="h4" fontWeight={800} sx={{ mb: 2 }}>{content.sectionTitle}</Typography>
                                  {content.body.map((paragraph) => <Typography key={paragraph} color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.8 }}>{paragraph}</Typography>)}
                                </>}
                                <Button variant="contained" onClick={() => navigate("/contact")} sx={{ mt: 2, bgcolor: "#FFD600", color: "#111", fontWeight: 800, px: 4, py: 1.3, "&:hover": { bgcolor: "#e5c400" } }}>
                                  {content.requestSubmit}
                                </Button>
                              </Box>
                              {service.personInRequest && (
                                <Box
                                  role={service.person.link ? "link" : undefined}
                                  tabIndex={service.person.link ? 0 : undefined}
                                  onClick={() => service.person.link && navigate(service.person.link)}
                                  onKeyDown={(event) => {
                                    if (service.person.link && (event.key === "Enter" || event.key === " ")) navigate(service.person.link);
                                  }}
                                  sx={{ bgcolor: "#F8FAFB", borderRadius: 3, overflow: "hidden", border: "1px solid #E4EAEA", cursor: service.person.link ? "pointer" : "default" }}
                                >
                                  <Box component="img" src={service.person.image} alt={content.person.name} sx={{ display: "block", width: "100%", height: { xs: 340, md: 430 }, objectFit: "cover", objectPosition: "center" }} />
                                  <Box dir={isArabic ? "rtl" : "ltr"} sx={{ p: 3, textAlign: "center" }}>
                                    <Typography variant="h6" fontWeight={800}>{content.person.name}</Typography>
                                    <Typography color="text.secondary">{content.person.role}</Typography>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          ) : <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: (service.requestImage || service.personInRequest) ? "minmax(0, 1.45fr) minmax(280px, .75fr)" : "minmax(0, 1fr)" }, gridTemplateAreas: { xs: '"form" "image"', md: (service.requestImage || service.personInRequest) ? '"form image"' : '"form"' }, gap: { xs: 3, md: 4 }, alignItems: "stretch", direction: "ltr" }}>
                            <Box
                              component="form"
                              onSubmit={submitEmployerRequest}
                              dir={isArabic ? "rtl" : "ltr"}
                              sx={{ gridArea: "form", ...(isArabic ? {
                                "& .MuiInputBase-input, & .MuiInputBase-inputMultiline": { textAlign: "right", direction: "rtl" },
                                "& .MuiInputLabel-root": { right: 14, left: "auto", transformOrigin: "top right" },
                                "& .MuiInputLabel-shrink": { transformOrigin: "top right" },
                                "& .MuiFormControlLabel-root": { mr: 0, ml: 2 },
                              } : {}) }}
                            >
                            <Grid container spacing={2.5}>
                              <Grid item xs={12} md={6}><TextField required fullWidth label={content.requestFields.company} value={requestForm.company} onChange={(e) => setRequestForm((form) => ({ ...form, company: e.target.value }))} /></Grid>
                              <Grid item xs={12} md={6}><TextField required fullWidth label={content.requestFields.contactName} value={requestForm.contactName} onChange={(e) => setRequestForm((form) => ({ ...form, contactName: e.target.value }))} /></Grid>
                              <Grid item xs={12} md={6}><TextField required type="email" fullWidth label={content.requestFields.email} value={requestForm.email} onChange={(e) => setRequestForm((form) => ({ ...form, email: e.target.value }))} /></Grid>
                              <Grid item xs={12} md={6}><TextField type="tel" fullWidth label={content.requestFields.phone} value={requestForm.phone} onChange={(e) => setRequestForm((form) => ({ ...form, phone: e.target.value }))} inputProps={{ dir: "ltr" }} /></Grid>
                              <Grid item xs={12} md={6}>
                                <FormControl><Typography fontWeight={700} sx={{ mb: 0.5 }}>{content.requestFields.lookingFor}</Typography><RadioGroup row value={requestForm.lookingFor} onChange={(e) => setRequestForm((form) => ({ ...form, lookingFor: e.target.value }))}>
                                  {Object.entries(content.requestOptions).map(([value, label]) => <FormControlLabel key={value} value={value} control={<Radio />} label={label} />)}
                                </RadioGroup></FormControl>
                              </Grid>
                              <Grid item xs={12} md={6}><TextField required fullWidth label={content.requestFields.position} value={requestForm.position} onChange={(e) => setRequestForm((form) => ({ ...form, position: e.target.value }))} /></Grid>
                              <Grid item xs={12} md={6}><TextField fullWidth label={content.requestFields.profile} value={requestForm.profile} onChange={(e) => setRequestForm((form) => ({ ...form, profile: e.target.value }))} /></Grid>
                              <Grid item xs={12}><TextField fullWidth multiline minRows={4} label={content.requestFields.message} value={requestForm.message} onChange={(e) => setRequestForm((form) => ({ ...form, message: e.target.value }))} /></Grid>
                              <Grid item xs={12}>{requestStatus.success && <Alert severity="success">{requestStatus.success}</Alert>}{requestStatus.error && <Alert severity="error">{requestStatus.error}</Alert>}</Grid>
                              <Grid item xs={12}><Button type="submit" variant="contained" disabled={requestStatus.submitting} sx={{ bgcolor: "#FFD600", color: "#111", fontWeight: 800, px: 4, py: 1.3, "&:hover": { bgcolor: "#e5c400" } }}>{requestStatus.submitting ? "..." : content.requestSubmit}</Button></Grid>
                            </Grid>
                            </Box>
                            {service.requestImage && (
                              <Box
                                component="img"
                                src={service.requestImage}
                                alt={content.requestTitle}
                                sx={{ gridArea: "image", display: "block", width: "100%", height: "100%", minHeight: { xs: 280, md: 560 }, maxHeight: { md: 720 }, objectFit: "cover", objectPosition: "center", borderRadius: 3, border: "1px solid #E4EAEA", bgcolor: "#F8FAFB" }}
                              />
                            )}
                            {service.personInRequest && (
                              <Box sx={{ gridArea: "image", bgcolor: "#F8FAFB", borderRadius: 3, overflow: "hidden", border: "1px solid #E4EAEA", alignSelf: "start" }}>
                                {service.person.image ? (
                                  <Box
                                    component="img"
                                    src={service.person.image}
                                    alt={content.person.name}
                                    onError={(event) => {
                                      event.currentTarget.style.display = "none";
                                      event.currentTarget.nextElementSibling.style.display = "grid";
                                    }}
                                    sx={{ display: "block", width: "100%", height: { xs: 340, md: 430 }, objectFit: "cover", objectPosition: "center" }}
                                  />
                                ) : null}
                                <Box sx={{ minHeight: { xs: 300, md: 430 }, display: service.person.image ? "none" : "grid", placeItems: "center", bgcolor: "#EEF3F4" }}>
                                  <Avatar sx={{ width: { xs: 120, md: 160 }, height: { xs: 120, md: 160 }, bgcolor: "#FFD600", color: "#111", fontSize: { xs: "2.5rem", md: "3.5rem" }, fontWeight: 800 }}>{content.person.initials}</Avatar>
                                </Box>
                                <Box sx={{ p: 3, textAlign: "center", direction: isArabic ? "rtl" : "ltr" }}>
                                  <Typography variant="h6" fontWeight={800}>{content.person.name}</Typography>
                                  <Typography color="text.secondary">{content.person.role}</Typography>
                                </Box>
                              </Box>
                            )}
                          </Box>}
                          <Typography variant="body2" sx={{ mt: 3, color: "text.secondary" }}>
                            {content.requestDirect}{" "}
                            <Box component="a" dir="ltr" href="tel:+4915125368005" sx={{ display: "inline-block", direction: "ltr", unicodeBidi: "isolate", color: "inherit", fontWeight: 700 }}>+49 1512 5368005</Box>
                            {" · "}{content.requestWrite}{" "}
                            <Box component="a" dir="ltr" href={`mailto:${service.contentKey === "servicePages.corporateRecruitment" ? RECRUITMENT_EMAIL : CONTACT_EMAIL}`} sx={{ display: "inline-block", direction: "ltr", unicodeBidi: "isolate", color: "inherit", fontWeight: 700 }}>{service.contentKey === "servicePages.corporateRecruitment" ? RECRUITMENT_EMAIL : CONTACT_EMAIL}</Box>
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          ) : (
            <Grid container spacing={3}>
              {content.points.map((point) => (
                <Grid item xs={12} md={4} key={point.title}>
                  <Box
                    sx={{
                      height: "100%",
                      bgcolor: "#F8FAFB",
                      border: "1px solid #E4EAEA",
                      borderRadius: 3,
                      p: { xs: 3, md: 4 },
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
                      {point.title}
                    </Typography>
                    <Typography color="text.secondary">{point.text}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </>
  );
};

export default ServiceLandingPage;
